import AuthAPI from '../api/auth';
import ContactAPI from '../api/contacts';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'message.reactions_updated': this.onMessageReactionsUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'contact.collaborators_changed': this.onCollaboratorsChanged,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'conversation.pins_changed': this.onConversationPinsChanged,
      'account.cache_invalidated': this.onCacheInvalidate,
      'ai_assist.message.created': this.onAiAssistMessageCreated,
      'ai_assist.operation.updated': this.onAiAssistOperationUpdated,
      'whatsapp.template.updated': this.onWhatsappTemplateUpdated,
      'calendar.event.created': this.onCalendarEventChanged,
      'calendar.event.updated': this.onCalendarEventChanged,
      'calendar.event.deleted': this.onCalendarEventChanged,
      'calendar.sync.updated': this.onCalendarEventChanged,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = data => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT, data);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onConversationPinsChanged = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit('conversation.pins_changed');
  };

  onMessageReactionsUpdated = data => {
    if (!this.isAValidEvent(data)) return;
    this.app.$store.commit('UPDATE_MESSAGE_REACTIONS', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  // The template event is UI-only and does not mutate the cable instance.
  // eslint-disable-next-line class-methods-use-this
  onWhatsappTemplateUpdated = data => {
    emitter.emit('whatsapp_template_updated', data);
  };

  // Calendar payloads intentionally contain identifiers only. The active
  // calendar view refetches through its authorized range endpoint.
  onCalendarEventChanged = data => {
    if (!this.isAValidEvent(data)) return;
    this.app.$store.dispatch('notifications/clear');
    this.app.$store.dispatch('notifications/index');
    this.app.$store.dispatch('notifications/unReadCount');
    emitter.emit('calendar.event.changed', data);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onCollaboratorsChanged = async data => {
    if (!this.isAValidEvent(data)) return;
    try {
      const response = await ContactAPI.show(data.id);
      this.onContactUpdate(response.data.payload);
    } catch (error) {
      if ([401, 403, 404].includes(error.response?.status))
        this.onContactDelete(data);
    }
    this.app.$store.dispatch('notifications/clear');
    this.app.$store.dispatch('notifications/index');
    this.app.$store.dispatch('notifications/unReadCount');
    this.fetchConversationStats();
    emitter.emit('contact.collaborators_changed', data);
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
    if (data.labels) {
      this.app.$store.dispatch('syncContactLabels', {
        contactId: data.id,
        labels: data.labels,
      });
      this.app.$store.dispatch('contactLabels/setContactLabel', {
        id: data.id,
        data: data.labels,
      });
      this.fetchConversationStats();
    }
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onAiAssistMessageCreated = data => {
    this.app.$store.dispatch('aiAssistMessages/upsert', data);
  };

  onAiAssistOperationUpdated = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit('ai_assist.operation.updated', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
